import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import CropModal from '../CropModal';
import * as styles from './FigureImageView.css';

export default function FigureImageView({
  node,
  updateAttributes,
  selected,
  deleteNode,
}: NodeViewProps) {
  const { src, alt, width, caption, layout, cropX, cropY, cropW, cropH } = node.attrs;
  const hasCrop = !(cropX === 0 && cropY === 0 && cropW === 100 && cropH === 100);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);

  // Show toolbar when selected or clicked; hide on outside click
  useEffect(() => {
    if (selected) setShowToolbar(true);
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowToolbar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set TipTap's outer ReactRenderer wrapper to inline-block for row layout.
  // DOM: <div class="node-figureImage"> (outer) > <div data-node-view-wrapper> (NodeViewWrapper)
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    // The outer wrapper is either the parent (if TipTap adds one) or the element itself
    const outerWrapper = el.parentElement;
    if (outerWrapper && outerWrapper.classList.contains('node-figureImage')) {
      if (layout === 'row') {
        outerWrapper.style.display = 'inline-block';
        outerWrapper.style.verticalAlign = 'top';
        outerWrapper.style.width = `${width}%`;
      } else {
        outerWrapper.style.display = '';
        outerWrapper.style.verticalAlign = '';
        outerWrapper.style.width = '';
      }
    }
  }, [layout, width]);

  const handleCaptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateAttributes({ caption: e.target.value });
    },
    [updateAttributes]
  );

  const handleResize = useCallback(
    (e: React.MouseEvent, preserveAspectRatio: boolean) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const parentEl = containerRef.current?.parentElement;
      if (!parentEl) return;

      const parentWidth = parentEl.getBoundingClientRect().width;
      const startWidthPx = (width / 100) * parentWidth;
      const imgEl = imageRef.current;
      const aspectRatio = imgEl
        ? imgEl.naturalWidth / imgEl.naturalHeight
        : 1;

      setIsResizing(true);

      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const newWidthPx = Math.max(50, startWidthPx + dx);
        const newWidthPct = Math.min(100, Math.round((newWidthPx / parentWidth) * 100));
        updateAttributes({ width: newWidthPct });
      };

      const onMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [width, updateAttributes]
  );

  const isRow = layout === 'row';

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      data-layout={layout}
      className={isRow ? styles.wrapperRow : styles.wrapper}
      onClick={() => setShowToolbar(true)}
    >
      {/* Floating toolbar */}
      {showToolbar && !isResizing && (
        <div className={styles.toolbar} contentEditable={false}>
          <button
            className={
              layout === 'full' ? styles.toolbarButtonActive : styles.toolbarButton
            }
            onClick={() => updateAttributes({ layout: 'full' })}
            type="button"
          >
            Full
          </button>
          <button
            className={
              layout === 'row' ? styles.toolbarButtonActive : styles.toolbarButton
            }
            onClick={() =>
              updateAttributes({ layout: 'row', width: width > 50 ? 48 : width })
            }
            type="button"
          >
            Row
          </button>
          <span style={{ width: 1, background: '#ddd', margin: '0 2px' }} />
          <button
            className={styles.toolbarButton}
            onClick={() => setShowCrop(true)}
            type="button"
          >
            Crop
          </button>
          <button
            className={styles.toolbarButtonDanger}
            onClick={deleteNode}
            type="button"
          >
            Delete
          </button>
        </div>
      )}

      {/* Image with resize handles */}
      <div
        ref={containerRef}
        className={styles.imageContainer}
        style={{ width: isRow ? '100%' : `${width}%` }}
      >
        {/* Drag handle - TipTap uses data-drag-handle to initiate proper node move */}
        <div
          className={styles.dragHandle}
          contentEditable={false}
          data-drag-handle=""
          draggable={true}
        >
          ⠿
        </div>

        {hasCrop ? (
          <div
            style={{
              overflow: 'hidden',
              width: '100%',
              // Aspect ratio of the cropped area relative to the full image
              // We don't know the natural aspect here, so use padding-bottom trick via a wrapper
            }}
            className={showToolbar ? styles.imageSelected : undefined}
          >
            <img
              ref={imageRef}
              src={src}
              alt={alt}
              className={styles.image}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              style={{
                width: `${10000 / cropW}%`,
                maxWidth: 'none',
                marginLeft: `${-(cropX / cropW) * 100}%`,
                marginTop: `${-(cropY / cropH) * 100}%`,
                display: 'block',
              }}
            />
          </div>
        ) : (
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            className={`${styles.image} ${showToolbar ? styles.imageSelected : ''}`}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        )}

        {/* Resize handles */}
        {showToolbar && (
          <>
            <div
              className={styles.handleBottomRight}
              onMouseDown={(e) => handleResize(e, true)}
            />
            <div
              className={styles.handleBottomLeft}
              onMouseDown={(e) => {
                // For left handles, invert the delta
                e.preventDefault();
                e.stopPropagation();
                const startX = e.clientX;
                const parentEl = containerRef.current?.parentElement;
                if (!parentEl) return;
                const parentWidth = parentEl.getBoundingClientRect().width;
                const startWidthPx = (width / 100) * parentWidth;
                setIsResizing(true);
                const onMouseMove = (ev: MouseEvent) => {
                  const dx = startX - ev.clientX;
                  const newWidthPx = Math.max(50, startWidthPx + dx);
                  const newWidthPct = Math.min(
                    100,
                    Math.round((newWidthPx / parentWidth) * 100)
                  );
                  updateAttributes({ width: newWidthPct });
                };
                const onMouseUp = () => {
                  setIsResizing(false);
                  window.removeEventListener('mousemove', onMouseMove);
                  window.removeEventListener('mouseup', onMouseUp);
                };
                window.addEventListener('mousemove', onMouseMove);
                window.addEventListener('mouseup', onMouseUp);
              }}
            />
            <div
              className={styles.handleRight}
              onMouseDown={(e) => handleResize(e, false)}
            />
            <div
              className={styles.handleLeft}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const startX = e.clientX;
                const parentEl = containerRef.current?.parentElement;
                if (!parentEl) return;
                const parentWidth = parentEl.getBoundingClientRect().width;
                const startWidthPx = (width / 100) * parentWidth;
                setIsResizing(true);
                const onMouseMove = (ev: MouseEvent) => {
                  const dx = startX - ev.clientX;
                  const newWidthPx = Math.max(50, startWidthPx + dx);
                  const newWidthPct = Math.min(
                    100,
                    Math.round((newWidthPx / parentWidth) * 100)
                  );
                  updateAttributes({ width: newWidthPct });
                };
                const onMouseUp = () => {
                  setIsResizing(false);
                  window.removeEventListener('mousemove', onMouseMove);
                  window.removeEventListener('mouseup', onMouseUp);
                };
                window.addEventListener('mousemove', onMouseMove);
                window.addEventListener('mouseup', onMouseUp);
              }}
            />
            <div
              className={styles.handleTopRight}
              onMouseDown={(e) => handleResize(e, true)}
            />
            <div
              className={styles.handleTopLeft}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const startX = e.clientX;
                const parentEl = containerRef.current?.parentElement;
                if (!parentEl) return;
                const parentWidth = parentEl.getBoundingClientRect().width;
                const startWidthPx = (width / 100) * parentWidth;
                setIsResizing(true);
                const onMouseMove = (ev: MouseEvent) => {
                  const dx = startX - ev.clientX;
                  const newWidthPx = Math.max(50, startWidthPx + dx);
                  const newWidthPct = Math.min(
                    100,
                    Math.round((newWidthPx / parentWidth) * 100)
                  );
                  updateAttributes({ width: newWidthPct });
                };
                const onMouseUp = () => {
                  setIsResizing(false);
                  window.removeEventListener('mousemove', onMouseMove);
                  window.removeEventListener('mouseup', onMouseUp);
                };
                window.addEventListener('mousemove', onMouseMove);
                window.addEventListener('mouseup', onMouseUp);
              }}
            />
          </>
        )}
      </div>

      {/* Caption input */}
      <input
        className={styles.caption}
        value={caption || ''}
        onChange={handleCaptionChange}
        placeholder="Lisa pealdis..."
        contentEditable={false}
      />

      {/* Crop modal — non-destructive, sets crop mask attributes */}
      {showCrop && (
        <CropModal
          imageSrc={src}
          initialCrop={{ x: cropX, y: cropY, w: cropW, h: cropH }}
          onCancel={() => setShowCrop(false)}
          onConfirm={(crop) => {
            updateAttributes({
              cropX: Math.round(crop.x * 10) / 10,
              cropY: Math.round(crop.y * 10) / 10,
              cropW: Math.round(crop.w * 10) / 10,
              cropH: Math.round(crop.h * 10) / 10,
            });
            setShowCrop(false);
          }}
        />
      )}
    </NodeViewWrapper>
  );
}
